/**
 * The riser diagram's geometry (spec 2026-10-19-03 §2): the building in
 * section, floors as horizontal bands stacked bottom up, each column a vertical
 * line placed by its building-frame `x`. Pure, so the stacking, the spacing and
 * the dashed gaps are unit-tested without a DOM.
 */
import type { components } from '../../api/schema'

export type RiserDiagram = components['schemas']['RiserDiagram']
export type RiserDiagramColumn = components['schemas']['RiserDiagramColumn']
export type RiserDiagramLevel = components['schemas']['RiserDiagramLevel']

/** Left gutter holding the floor labels, in pixels. */
export const LABEL_WIDTH = 76
/** Room above the top band for the column numbers. */
export const HEADER_HEIGHT = 28
const SIDE_PAD = 36
const MIN_COLUMN_GAP = 30
const UNIFORM_BAND = 56
const MIN_BAND = 44
const MAX_BAND = 110
const PX_PER_METRE = 18

export interface BandLayout {
    level: RiserDiagramLevel
    index: number
    y: number
    height: number
    /** Where a column crosses the band. */
    mid: number
}

export interface ColumnSegment {
    y1: number
    y2: number
    /** Continuity not drawn: one end of the stretch has no riser. */
    dashed: boolean
    /** The level whose missing riser makes it dashed, for the gap's test id. */
    gapLevelUid?: string
}

export interface ColumnLayout {
    column: RiserDiagramColumn
    /** 1-based, the badge the legend repeats. */
    number: number
    x: number
    segments: ColumnSegment[]
}

export interface DiagramLayout {
    width: number
    height: number
    bands: BandLayout[]
    columns: ColumnLayout[]
}

/**
 * Bands are as tall as the floor's ceiling height when known; a floor without
 * one takes the mean of the known ones, and with none known every band is the
 * same.
 */
function bandHeights(levels: RiserDiagramLevel[]): number[] {
    const known = levels.map((l) => l.ceiling_height_m).filter((h): h is number => typeof h === 'number' && h > 0)
    if (!known.length) return levels.map(() => UNIFORM_BAND)
    const mean = known.reduce((a, b) => a + b, 0) / known.length
    return levels.map((l) => {
        const h = typeof l.ceiling_height_m === 'number' && l.ceiling_height_m > 0 ? l.ceiling_height_m : mean
        return Math.min(MAX_BAND, Math.max(MIN_BAND, h * PX_PER_METRE))
    })
}

/** Placed columns left to right, then the ones no plan places, by name. */
export function byColumnX(a: RiserDiagramColumn, b: RiserDiagramColumn): number {
    if (a.x != null && b.x != null && a.x !== b.x) return a.x - b.x
    if (a.x != null && b.x == null) return -1
    if (a.x == null && b.x != null) return 1
    return (a.name ?? '').localeCompare(b.name ?? '')
}

/**
 * Columns sit at their building-frame x scaled into the drawing, then pushed
 * apart to a readable gap; when the width cannot hold that, they share it
 * evenly, still in x order.
 */
function columnXs(columns: RiserDiagramColumn[], width: number): number[] {
    const x0 = LABEL_WIDTH + SIDE_PAD
    const x1 = Math.max(x0, width - SIDE_PAD)
    if (!columns.length) return []
    if (columns.length === 1) return [(x0 + x1) / 2]
    const span = x1 - x0
    // A column known only by declaration has no x (spec 2026-10-25-21): with
    // one in the set, positions cannot be scaled, so they share the width.
    if (span / (columns.length - 1) < MIN_COLUMN_GAP || columns.some((c) => c.x == null)) {
        return columns.map((_, i) => x0 + (span * i) / (columns.length - 1))
    }
    const xs = columns.map((c) => c.x ?? 0)
    const lo = Math.min(...xs)
    const hi = Math.max(...xs)
    const scaled = xs.map((x) => (hi > lo ? x0 + ((x - lo) / (hi - lo)) * span : (x0 + x1) / 2))
    // Forward then backward pass: keep the gap, stay inside [x0, x1].
    for (let i = 1; i < scaled.length; i++) scaled[i] = Math.max(scaled[i], scaled[i - 1] + MIN_COLUMN_GAP)
    scaled[scaled.length - 1] = Math.min(scaled[scaled.length - 1], x1)
    for (let i = scaled.length - 2; i >= 0; i--) scaled[i] = Math.min(scaled[i], scaled[i + 1] - MIN_COLUMN_GAP)
    return scaled
}

/**
 * `hiddenNetworks` are the network codes the layer panel hides (a whole family
 * is all its codes), the same vocabulary as the floor plans' layer state.
 */
export function layoutRiserDiagram(diagram: RiserDiagram, width: number, hiddenNetworks: ReadonlySet<string> = new Set()): DiagramLayout {
    const levels = diagram.levels
    const heights = bandHeights(levels)
    // Highest floor on top: walk the levels from the last.
    const bands: BandLayout[] = []
    let y = HEADER_HEIGHT
    for (let i = levels.length - 1; i >= 0; i--) {
        bands.push({ level: levels[i], index: i, y, height: heights[i], mid: y + heights[i] / 2 })
        y += heights[i]
    }
    const midOf = new Map(bands.map((b) => [b.index, b.mid]))
    const indexOf = new Map(levels.map((l, i) => [l.asset_uid, i]))

    const visible = diagram.columns
        .filter((c) => !hiddenNetworks.has(c.network))
        .slice()
        .sort(byColumnX)
    const xs = columnXs(visible, width)
    const columns = visible.map((column, i): ColumnLayout => {
        const withRiser = new Set<number>()
        for (const l of column.levels) {
            const index = indexOf.get(l.level_uid)
            // A declared crossing documents the level as a drawn riser does
            // (spec 2026-10-25-21).
            if ((l.riser || l.declared) && index !== undefined) withRiser.add(index)
        }
        const segments: ColumnSegment[] = []
        if (withRiser.size) {
            const lo = Math.min(...withRiser)
            const hi = Math.max(...withRiser)
            for (let li = lo; li < hi; li++) {
                const dashed = !withRiser.has(li) || !withRiser.has(li + 1)
                const gap = !withRiser.has(li + 1) ? li + 1 : !withRiser.has(li) ? li : undefined
                segments.push({
                    y1: midOf.get(li + 1) ?? 0,
                    y2: midOf.get(li) ?? 0,
                    dashed,
                    ...(dashed && gap !== undefined ? { gapLevelUid: levels[gap].asset_uid } : {}),
                })
            }
        }
        return { column, number: diagram.columns.indexOf(column) + 1, x: xs[i], segments }
    })
    return { width, height: y + 8, bands, columns }
}

/**
 * Where a click on a level band should centre that floor's plan: the riser of
 * the visible column nearest the click, or null when no visible column is
 * drawn on that floor (the plan then opens on its default view).
 */
export function levelClickTarget(layout: DiagramLayout, levelUid: string, clickX: number): { x: number; y: number } | null {
    let best: { x: number; y: number } | null = null
    let bestDistance = Infinity
    for (const c of layout.columns) {
        const riser = c.column.levels.find((l) => l.level_uid === levelUid)?.riser
        if (!riser) continue
        const distance = Math.abs(c.x - clickX)
        if (distance < bestDistance) {
            bestDistance = distance
            best = { x: riser.at.x, y: riser.at.y }
        }
    }
    return best
}
